import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { User, CreditCard, FileText, Loader2, Check, Coins, Clock, CheckCircle, XCircle, ExternalLink } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const FUNCTION_LIST = [
  { id: "procurement", name: "Procurement" },
  { id: "finance",     name: "Finance" },
  { id: "marketing",   name: "Marketing" },
  { id: "hr",          name: "HR" },
  { id: "sales",       name: "Sales" },
  { id: "operations",  name: "Operations" },
  { id: "general",     name: "General" },
];

const PLAN_INFO = {
  free:   { label: "Free",            max: 0  },
  single: { label: "Single Function", max: 1  },
  multi:  { label: "Multi-Function",  max: 3  },
  full:   { label: "Full Access",     max: -1 },
};

const ProfilePage = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [selectedFunctions, setSelectedFunctions] = useState(
    Array.isArray(user?.accessible_functions) ? user.accessible_functions : []
  );
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [submissions, setSubmissions] = useState([]);

  const plan = PLAN_INFO[user?.subscription_plan] || PLAN_INFO.free;
  const maxFunctions = plan.max;

  useEffect(() => {
    axios.get(`${API}/contributor/submissions`, { withCredentials: true })
      .then(res => setSubmissions(res.data))
      .catch(() => {});
  }, []);

  const toggleFunction = (fnId) => {
    if (user?.accessible_functions === "all") return;
    if (selectedFunctions.includes(fnId)) {
      setSelectedFunctions(prev => prev.filter(f => f !== fnId));
    } else {
      if (maxFunctions === 0) {
        setError("Please upgrade your plan to select functions.");
        return;
      }
      if (maxFunctions !== -1 && selectedFunctions.length >= maxFunctions) {
        setError(`Your plan allows max ${maxFunctions} function${maxFunctions > 1 ? "s" : ""}.`);
        return;
      }
      setSelectedFunctions(prev => [...prev, fnId]);
    }
    setError("");
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const body = { name };
      if (user?.accessible_functions !== "all") {
        body.accessible_functions = selectedFunctions;
      }
      await axios.put(`${API}/users/profile`, body, { withCredentials: true });
      await refreshUser();
      setSuccess("Profile updated successfully.");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save profile.");
    } finally {
      setSaving(false);
    }
  };

  const statusColor = {
    submitted:    "bg-yellow-100 text-yellow-700",
    under_review: "bg-blue-100 text-blue-700",
    approved:     "bg-emerald-100 text-emerald-700",
    rejected:     "bg-red-100 text-red-700",
  };

  const statusLabel = {
    submitted:    "Awaiting Review",
    under_review: "Under Review",
    approved:     "Approved & Live",
    rejected:     "Not Approved",
  };

  const StatusIcon = ({ status }) => {
    if (status === "approved")     return <CheckCircle size={14} className="text-emerald-600" />;
    if (status === "rejected")     return <XCircle size={14} className="text-red-500" />;
    if (status === "under_review") return <Clock size={14} className="text-blue-500" />;
    return <Clock size={14} className="text-yellow-500" />;
  };

  const STEPS = ["submitted", "under_review", "approved"];
  const getStepIndex = (status) => {
    if (status === "rejected") return -1;
    return STEPS.indexOf(status);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-bold text-slate-900 mb-6">Account Settings</h1>

        <Tabs defaultValue="profile">
          <TabsList className="mb-6">
            <TabsTrigger value="profile"><User size={14} className="mr-1.5" /> Profile</TabsTrigger>
            <TabsTrigger value="plan"><CreditCard size={14} className="mr-1.5" /> Plan & Credits</TabsTrigger>
            <TabsTrigger value="submissions"><FileText size={14} className="mr-1.5" /> My Submissions</TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
              {success && <Alert className="border-emerald-200 bg-emerald-50"><AlertDescription className="text-emerald-700">{success}</AlertDescription></Alert>}
              {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

              <div>
                <Label>Full Name</Label>
                <Input
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className="mt-1"
                  data-testid="profile-name-input"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input value={user?.email || ""} disabled className="mt-1 bg-slate-50" />
              </div>
              <div>
                <Label>Login Method</Label>
                <Input value={user?.login_type || "email"} disabled className="mt-1 bg-slate-50 capitalize" />
              </div>

              {/* Function Selection */}
              {user?.accessible_functions !== "all" && user?.subscription_plan !== "free" && (
                <div>
                  <Label className="block mb-2">
                    Your Functions{" "}
                    <span className="text-slate-400 font-normal text-xs">
                      (select up to {maxFunctions})
                    </span>
                  </Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {FUNCTION_LIST.map(fn => {
                      const selected = selectedFunctions.includes(fn.id);
                      return (
                        <button
                          key={fn.id}
                          onClick={() => toggleFunction(fn.id)}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all ${
                            selected
                              ? "bg-blue-50 border-blue-400 text-blue-700 font-medium"
                              : "bg-white border-slate-200 text-slate-600 hover:border-blue-300"
                          }`}
                          data-testid={`select-function-${fn.id}`}
                        >
                          {selected && <Check size={13} />}
                          {fn.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="save-profile-btn"
              >
                {saving ? <><Loader2 className="animate-spin mr-2 w-4 h-4" /> Saving...</> : "Save Changes"}
              </Button>
            </div>
          </TabsContent>

          {/* Plan Tab */}
          <TabsContent value="plan">
            <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Current Plan</p>
                  <p className="font-bold text-xl text-slate-900 mt-0.5">{plan.label}</p>
                </div>
                <Button
                  onClick={() => navigate("/pricing")}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="upgrade-plan-profile-btn"
                >
                  Upgrade Plan
                </Button>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Credit Balance</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Coins size={18} className="text-blue-600" />
                    <span className="font-bold text-2xl text-slate-900" data-testid="profile-credits">{user?.credits ?? 0}</span>
                    <span className="text-slate-400 text-sm">credits</span>
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={() => navigate("/pricing")}
                  data-testid="buy-credits-profile-btn"
                >
                  Buy Credits
                </Button>
              </div>
              <Separator />
              <div>
                <p className="text-sm text-slate-500 mb-3">Account Details</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Account Type</span>
                    <span className="font-medium capitalize">{user?.account_type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Member Since</span>
                    <span className="font-medium">
                      {user?.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Submissions Tab */}
          <TabsContent value="submissions">
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              {submissions.length === 0 ? (
                <div className="text-center py-10">
                  <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FileText size={24} className="text-slate-400" />
                  </div>
                  <p className="font-medium text-slate-700 mb-1">No submissions yet</p>
                  <p className="text-sm text-slate-500 mb-5">Share your AI expertise with the community</p>
                  <Button onClick={() => navigate("/contribute")} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="contribute-cta-btn">
                    Contribute an Application
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Stats row */}
                  <div className="grid grid-cols-3 gap-3 pb-4 border-b border-slate-100">
                    {[
                      { label: "Total Submitted", count: submissions.length, color: "text-slate-900" },
                      { label: "Pending Review", count: submissions.filter(s => s.status === "submitted" || s.status === "under_review").length, color: "text-yellow-600" },
                      { label: "Approved & Live", count: submissions.filter(s => s.status === "approved").length, color: "text-emerald-600" },
                    ].map(stat => (
                      <div key={stat.label} className="text-center bg-slate-50 rounded-xl p-3 border border-slate-100">
                        <p className={`text-2xl font-bold ${stat.color}`}>{stat.count}</p>
                        <p className="text-xs text-slate-500 mt-0.5 leading-tight">{stat.label}</p>
                      </div>
                    ))}
                  </div>

                  {/* Submission cards */}
                  {submissions.map(sub => {
                    const stepIdx = getStepIndex(sub.status);
                    const isRejected = sub.status === "rejected";
                    return (
                      <div
                        key={sub.sub_id}
                        className={`border rounded-xl p-4 ${isRejected ? "border-red-100 bg-red-50/30" : sub.status === "approved" ? "border-emerald-100 bg-emerald-50/20" : "border-slate-100"}`}
                        data-testid={`submission-card-${sub.sub_id}`}
                      >
                        {/* Header */}
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div>
                            <p className="font-semibold text-slate-900 text-sm">{sub.title}</p>
                            <p className="text-xs text-slate-500 mt-0.5 capitalize">{sub.function_id} · {sub.category}</p>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <StatusIcon status={sub.status} />
                            <Badge className={`text-xs border-0 ${statusColor[sub.status] || "bg-slate-100 text-slate-600"}`}>
                              {statusLabel[sub.status] || sub.status}
                            </Badge>
                          </div>
                        </div>

                        {/* Progress steps (not shown for rejected) */}
                        {!isRejected && (
                          <div className="flex items-center gap-1 mb-3">
                            {STEPS.map((step, i) => (
                              <React.Fragment key={step}>
                                <div className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full transition-all ${
                                  i <= stepIdx
                                    ? i === stepIdx
                                      ? "bg-blue-100 text-blue-700"
                                      : "bg-emerald-100 text-emerald-700"
                                    : "bg-slate-100 text-slate-400"
                                }`}>
                                  {i < stepIdx && <Check size={10} />}
                                  {step === "submitted" ? "Submitted" : step === "under_review" ? "Under Review" : "Published"}
                                </div>
                                {i < STEPS.length - 1 && (
                                  <div className={`flex-1 h-0.5 rounded ${i < stepIdx ? "bg-emerald-300" : "bg-slate-200"}`} />
                                )}
                              </React.Fragment>
                            ))}
                          </div>
                        )}

                        {/* Reviewer notes (for rejected or approved with notes) */}
                        {sub.reviewer_notes && (
                          <div className={`text-xs p-3 rounded-lg mb-3 ${isRejected ? "bg-red-50 border border-red-100 text-red-700" : "bg-blue-50 border border-blue-100 text-blue-700"}`}>
                            <p className="font-semibold mb-0.5">{isRejected ? "Reviewer feedback:" : "Note from reviewer:"}</p>
                            {sub.reviewer_notes}
                          </div>
                        )}

                        {/* Footer */}
                        <div className="flex items-center justify-between">
                          <div className="text-xs text-slate-400 flex items-center gap-3">
                            <span>Submitted {new Date(sub.created_at).toLocaleDateString()}</span>
                            {sub.consent_flag && (
                              <span className="flex items-center gap-1 text-emerald-600">
                                <Check size={10} /> IP consent recorded
                              </span>
                            )}
                          </div>
                          {sub.status === "approved" && sub.approved_app_id && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/app/${sub.approved_app_id}`)}
                              className="text-xs h-7 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                              data-testid={`view-approved-app-${sub.sub_id}`}
                            >
                              <ExternalLink size={11} className="mr-1" /> View Live App
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate("/contribute")}
                    className="w-full border-dashed text-slate-500 hover:text-blue-600 hover:border-blue-300"
                  >
                    + Submit another application
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ProfilePage;
